import { useCallback, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Upload, FileText, Download, Loader2 } from 'lucide-react';
import { importService } from '../../services/import-service';
import { validateFile } from '../../utils/asset-validator';

interface CSVUploadZoneProps {
  readonly onFileUpload: (file: File) => void;
}

export default function CSVUploadZone({ onFileUpload }: Readonly<CSVUploadZoneProps>) {
  const [isDragActive, setIsDragActive] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDownloadingTemplate, setIsDownloadingTemplate] = useState(false);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
    setError(null);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      const validation = validateFile(file);
      
      if (!validation.valid) {
        setError(validation.error ?? 'Invalid file');
        return;
      }

      onFileUpload(file);
    }
  }, [onFileUpload]);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setError(null);
    const files = e.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      const validation = validateFile(file);
      
      if (!validation.valid) {
        setError(validation.error ?? 'Invalid file');
        return;
      }

      onFileUpload(file);
    }
  }, [onFileUpload]);

  const handleDownloadTemplate = async () => {
    setIsDownloadingTemplate(true);
    setError(null);
    try {
      await importService.downloadTemplateCSV();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to download template');
    } finally {
      setIsDownloadingTemplate(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="csv-upload-zone">
      {/* Upload Zone */}
      <Card>
        <CardContent className="pt-6">
          <div
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            className={`
              border-2 border-dashed rounded-lg p-12 text-center cursor-pointer
              transition-colors
              ${isDragActive ? 'border-primary bg-primary/5' : 'border-muted-foreground/25'}
              hover:border-primary hover:bg-primary/5
            `}
          >
            <input
              type="file"
              accept=".csv"
              onChange={handleFileSelect}
              className="hidden"
              id="csv-file-input"
              data-testid="csv-file-input"
            />
            <label htmlFor="csv-file-input" className="cursor-pointer">
              <Upload className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
              <p className="text-lg font-semibold mb-2">
                Drag and drop your CSV file here
              </p>
              <p className="text-sm text-muted-foreground mb-4">or</p>
              <Button type="button" variant="secondary" asChild>
                <span>Browse Files</span>
              </Button>
              <p className="text-xs text-muted-foreground mt-4">
                Accepted format: .csv
              </p>
            </label>
          </div>
          {error && (
            <div className="mt-4 p-3 bg-destructive/10 border border-destructive rounded-md" data-testid="csv-upload-error">
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Template Section */}
      <Card className="bg-muted/50">
        <CardContent className="pt-6">
          <div className="flex items-start gap-4">
            <FileText className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
            <div className="flex-1">
              <h3 className="font-semibold mb-2">Need the correct format?</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Download our CSV template with all required columns and example data to ensure
                your import is successful.
              </p>
              <Button variant="outline" onClick={handleDownloadTemplate} disabled={isDownloadingTemplate} data-testid="download-template-btn">
                {isDownloadingTemplate ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Download className="mr-2 h-4 w-4" />
                )}
                {isDownloadingTemplate ? 'Downloading...' : 'Download Template'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
